import { z } from 'zod';
import { optionTypeSchema } from '../../../helper';
import { packLineSchema, IPackLine } from './packLine';
import { ISite } from '../site/site';

/**
 * Schema for validating pack line form data.
 * Extends the base pack line schema with form-specific validations.
 *
 * @property {string} code - Pack line code (max 5 characters)
 * @property {string} name - Pack line name (max 255 characters)
 * @property {OptionType} site - Site selection with label/value pair
 */
export const packLineFormSchema = packLineSchema.extend({
    site: optionTypeSchema,
}).omit({
    // Omit the original ID field
    siteId: true,
});

/**
 * Default schema for pack line form initialization.
 * Extends the form schema by making required fields optional with defaults.
 *
 * @property {string} [code] - Optional pack line code
 * @property {string} [name] - Optional pack line name
 * @property {OptionType} [site] - Optional site selection
 */
export const packLineDefaultFormSchema = packLineFormSchema.extend({
    code: z.string().optional().default(''),
    name: z.string().optional().default(''),
    site: optionTypeSchema.optional(),
});

/**
 * TypeScript interface for pack line form values.
 * Generated from the pack line form schema for type safety.
 */
export type IPackLineFormValues = z.infer<typeof packLineFormSchema>;

/**
 * TypeScript interface for pack line default form values.
 * Generated from the pack line default form schema for type safety.
 */
export type IPackLineDefaultFormValues = z.infer<typeof packLineDefaultFormSchema>;

/**
 * Converts a Pack Line model to form values for editing.
 * If no pack line is provided, returns default form values.
 *
 * @param {IPackLine} [packLine] - Optional pack line model to convert
 * @param {Array<ISite>} [sites] - Array of sites to use for mapping
 * @returns {IPackLineFormValues | IPackLineDefaultFormValues} Form values ready for editing
 */
export const PackLineFormValues = (
    packLine ?: IPackLine,
    sites ?: Array<ISite>
) : IPackLineFormValues | IPackLineDefaultFormValues => {
    // Handle the default case (new pack line)
    if (!packLine) {
        return packLineDefaultFormSchema.parse({});
    }

    const { siteId, ...rest } = packLine;

    // Transform the IPackLine data into IPackLineFormValues structure
    return packLineFormSchema.parse({
        ...rest,
        site: siteId
            ? {
                label: sites?.find(x => x.id === siteId) ? `(${sites?.find(x => x.id === siteId)?.code}) ${sites?.find(x => x.id === siteId)?.description}` : '',
                value: siteId,
            }
            : undefined,
    });
};

/**
 * Validates pack line form values against the schema.
 * Logs validation errors to console if validation fails.
 *
 * @param {IPackLineFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation succeeds, false otherwise
 */
export const validatePackLineForm = (formValues : IPackLineFormValues) : boolean => {
    try {
        packLineFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Pack line form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
